import i18n from '../../../i18n/config';
import { buildGenogramSemanticContext } from '../../../domains/genogram/services/GenogramSemanticContext';
import { computeOrgReportCounts } from '../../../domains/orgchart/services/orgChartReportCounts';
import { StructuredQA } from '../qa/StructuredQA';
import { QueryExecutor } from '../qa/QueryExecutor';
import type { AgentMessage, AgentToolCall, AgentToolResult, IAgentContext } from './IAgentTool';
import { StructuredToolMatcher } from './StructuredToolMatcher';
import { AgentToolRegistry } from './AgentToolRegistry';
import { buildClarifyingScopeText, inferExecutionPolicy } from './ExecutionPolicy';
import type { RdmMeta } from '../../../domains/rdm/models';

/** Org/genogram-only fast path — must not run on flowchart/process (Issue #1640). */
const STRUCTURED_QA_DIAGRAM_TYPES = new Set([
  'org',
  'orgchart',
  'org-chart',
  'genogram',
]);

const supportsStructuredOrgQueries = (diagramType: string): boolean =>
  STRUCTURED_QA_DIAGRAM_TYPES.has(diagramType);

const DIAGRAM_TYPE_TO_RDM_TYPE: Record<string, RdmMeta['type']> = {
  org: 'orgchart',
  orgchart: 'orgchart',
  'org-chart': 'orgchart',
  genogram: 'genogram',
  process: 'process',
  flowchart: 'process',
  bpmn: 'bpmn',
  network: 'network',
  table: 'table',
  inventory: 'inventory',
  generic: 'generic',
};

const PROCESS_DIAGRAM_TYPES = new Set(['process', 'flowchart', 'bpmn']);

export interface AgentLLMResponse {
  text: string;
  toolCalls: AgentToolCall[];
}

export interface AgentExecutorResult {
  text: string;
  toolResults: Array<{ call: AgentToolCall; result: AgentToolResult }>;
  source: 'structured' | 'llm' | 'none';
}

export type AgentLLMClient = (input: {
  messages: AgentMessage[];
  diagramType: string;
  diagramSummary: {
    nodeCount: number;
    edgeCount: number;
    fields: string[];
    departments?: string[];
    availableTitles?: string[];
    contextScope?: {
      requiredFields: string[];
      clarifyingQuestionScope: string;
      contextShape: string;
    };
    contextPayload?: Record<string, unknown>;
  };
  availableTools: string[];
  toolDefinitions: Array<{ name: string; description: string; parameters: Record<string, unknown>; tier: string; category: string }>;
  /** BCP-47 UI language; forwarded to the agent API for localized model replies. */
  uiLocale?: string;
  abortSignal?: AbortSignal;
}) => Promise<AgentLLMResponse>;

interface AgentExecutorOptions {
  registry: AgentToolRegistry;
  toolMatcher?: StructuredToolMatcher;
  structuredQA?: StructuredQA;
  queryExecutor?: QueryExecutor;
  llmClient?: AgentLLMClient;
  minStructuredConfidence?: number;
  rdmRuntimeLoader?: () => Promise<{
    rdmService: {
      fromRuntimeModel: (
        nodes: IAgentContext['diagramData']['nodes'],
        edges: IAgentContext['diagramData']['edges'],
        meta?: Partial<RdmMeta>,
      ) => string;
    };
  }>;
}

interface RdmSemanticContextPayload {
  mode: 'summary' | 'selected-neighborhood';
  type: RdmMeta['type'];
  source: 'runtime-model';
  content: string;
  truncated: boolean;
  nodeLimit: number;
  nodeCount: number;
  edgeCount: number;
  totalNodeCount: number;
  totalEdgeCount: number;
  selectedNodeIds?: string[];
}

interface RdmContextBuildResult {
  rdmSummary?: string;
  rdmContext?: RdmSemanticContextPayload;
  selectedRdmContext?: RdmSemanticContextPayload;
}

/**
 * Remove a top-level `layout { … }` block from serialized RDM. RDM is the
 * structural source; positions belong to the canvas, so the model never needs
 * (or should reproduce) layout. Brace-aware so nested entry braces don't
 * truncate the strip early.
 */
function stripRdmLayoutBlock(rdm: string): string {
  const start = rdm.search(/(^|\n)layout\s*\{/);
  if (start === -1) return rdm;
  const braceOpen = rdm.indexOf('{', start);
  if (braceOpen === -1) return rdm;
  let depth = 0;
  for (let i = braceOpen; i < rdm.length; i += 1) {
    const ch = rdm[i];
    if (ch === '{') depth += 1;
    else if (ch === '}') {
      depth -= 1;
      if (depth === 0) {
        const before = rdm.slice(0, start).replace(/\n+$/, '\n');
        const after = rdm.slice(i + 1).replace(/^\n+/, '\n');
        return (before + after).trimEnd() + '\n';
      }
    }
  }
  return rdm;
}

export class AgentExecutor {
  private registry: AgentToolRegistry;
  private matcher: StructuredToolMatcher;
  private structuredQA: StructuredQA;
  private queryExecutor: QueryExecutor;
  private llmClient?: AgentLLMClient;
  private minStructuredConfidence: number;
  private rdmRuntimeLoader: NonNullable<AgentExecutorOptions['rdmRuntimeLoader']>;

  constructor(options: AgentExecutorOptions) {
    this.registry = options.registry;
    this.matcher = options.toolMatcher ?? new StructuredToolMatcher();
    this.structuredQA = options.structuredQA ?? new StructuredQA();
    this.queryExecutor = options.queryExecutor ?? new QueryExecutor();
    this.llmClient = options.llmClient;
    this.minStructuredConfidence = options.minStructuredConfidence ?? 0.75;
    this.rdmRuntimeLoader = options.rdmRuntimeLoader ?? (() => import('../../../domains/rdm/runtime'));
  }

  private collectAvailableTitles(context: IAgentContext): string[] {
    const counts = new Map<string, number>();
    for (const node of context.diagramData.nodes) {
      const raw = (node.attrs?.title ?? node.props?.title ?? node.attrs?.role ?? node.props?.role) as unknown;
      if (typeof raw !== 'string') continue;
      const title = raw.trim();
      if (!title) continue;
      counts.set(title, (counts.get(title) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 30)
      .map(([title]) => title);
  }

  private getRdmType(diagramType: string): RdmMeta['type'] {
    return DIAGRAM_TYPE_TO_RDM_TYPE[diagramType] ?? 'generic';
  }

  private getRdmCharacterCap(rdmType: RdmMeta['type']): number {
    if (rdmType === 'network') return 8000;
    if (rdmType === 'process' || rdmType === 'bpmn') return 6000;
    return 3000;
  }

  private filterEdgesWithinNodes(
    nodes: IAgentContext['diagramData']['nodes'],
    edges: IAgentContext['diagramData']['edges'],
  ): IAgentContext['diagramData']['edges'] {
    const nodeIds = new Set(nodes.map((node) => node.id));
    return edges.filter((edge) => nodeIds.has(edge.from) && nodeIds.has(edge.to));
  }

  private async serializeRdmContext(
    nodes: IAgentContext['diagramData']['nodes'],
    edges: IAgentContext['diagramData']['edges'],
    options: {
      rdmType: RdmMeta['type'];
      mode: RdmSemanticContextPayload['mode'];
      nodeLimit: number;
      characterCap: number;
      totalNodeCount: number;
      totalEdgeCount: number;
      selectedNodeIds?: string[];
    },
  ): Promise<RdmSemanticContextPayload | undefined> {
    try {
      const { rdmService } = await this.rdmRuntimeLoader();
      let scopedNodes = nodes;
      let scopedEdges = edges;
      let content: string;
      while (true) {
        content = stripRdmLayoutBlock(rdmService.fromRuntimeModel(scopedNodes, scopedEdges, {
          type: options.rdmType,
        }));
        if (content.length <= options.characterCap) break;
        if (scopedNodes.length === 0) return undefined;
        scopedNodes = scopedNodes.slice(0, Math.floor(scopedNodes.length * 0.75));
        scopedEdges = this.filterEdgesWithinNodes(scopedNodes, edges);
      }
      const truncated = scopedNodes.length < options.totalNodeCount
        || scopedEdges.length < options.totalEdgeCount;

      return {
        mode: options.mode,
        type: options.rdmType,
        source: 'runtime-model',
        content,
        truncated,
        nodeLimit: options.nodeLimit,
        nodeCount: scopedNodes.length,
        edgeCount: scopedEdges.length,
        totalNodeCount: options.totalNodeCount,
        totalEdgeCount: options.totalEdgeCount,
        ...(options.selectedNodeIds ? { selectedNodeIds: options.selectedNodeIds } : {}),
      };
    } catch {
      return undefined;
    }
  }

  private async buildSelectedRdmContext(
    context: IAgentContext,
    rdmType: RdmMeta['type'],
  ): Promise<RdmSemanticContextPayload | undefined> {
    const selectedIds = new Set(context.selectedNodeIds.filter((id) => id.trim().length > 0));
    if (selectedIds.size === 0) return undefined;

    const neighborhoodIds = new Set(selectedIds);
    for (const edge of context.diagramData.edges) {
      if (selectedIds.has(edge.from) || selectedIds.has(edge.to)) {
        neighborhoodIds.add(edge.from);
        neighborhoodIds.add(edge.to);
      }
    }

    const selectedNodes = context.diagramData.nodes.filter((node) => selectedIds.has(node.id));
    const neighborNodes = context.diagramData.nodes.filter(
      (node) => !selectedIds.has(node.id) && neighborhoodIds.has(node.id),
    );
    const nodes = [...selectedNodes, ...neighborNodes].slice(0, 40);
    if (nodes.length === 0) return undefined;

    return this.serializeRdmContext(nodes, this.filterEdgesWithinNodes(nodes, context.diagramData.edges), {
      rdmType,
      mode: 'selected-neighborhood',
      nodeLimit: 40,
      characterCap: 3000,
      totalNodeCount: context.diagramData.nodes.length,
      totalEdgeCount: context.diagramData.edges.length,
      selectedNodeIds: Array.from(selectedIds),
    });
  }

  private async buildRdmContextPayload(context: IAgentContext): Promise<RdmContextBuildResult> {
    const rdmType = this.getRdmType(context.diagramType);
    const nodeLimit = rdmType === 'network' ? 120 : 80;
    const nodes = context.diagramData.nodes.slice(0, nodeLimit);
    if (nodes.length === 0) return {};
    const edges = this.filterEdgesWithinNodes(nodes, context.diagramData.edges);
    const [rdmContext, selectedRdmContext] = await Promise.all([
      this.serializeRdmContext(nodes, edges, {
        rdmType,
        mode: 'summary',
        nodeLimit,
        characterCap: this.getRdmCharacterCap(rdmType),
        totalNodeCount: context.diagramData.nodes.length,
        totalEdgeCount: context.diagramData.edges.length,
      }),
      this.buildSelectedRdmContext(context, rdmType),
    ]);

    return {
      rdmSummary: rdmContext?.content,
      ...(rdmContext ? { rdmContext } : {}),
      ...(selectedRdmContext ? { selectedRdmContext } : {}),
    };
  }

  private getContextShape(diagramType: string, isGenogram: boolean): string {
    if (isGenogram) return 'genogram-semantic-v2';
    if (diagramType === 'network') return 'rdm-first-network-context-v1';
    if (PROCESS_DIAGRAM_TYPES.has(diagramType)) return 'rdm-first-process-context-v1';
    return 'compact-org-context-v1';
  }

  private buildToolExecutionContext(
    context: IAgentContext,
    conversationHistory?: AgentMessage[],
  ): IAgentContext {
    const recentMessages = (conversationHistory ?? [])
      .slice(-8)
      .map((message) => {
        if (typeof message.content !== 'string') return null;
        const text = message.content.trim();
        if (!text) return null;
        return {
          role: message.role,
          text: text.slice(0, 2400),
        };
      })
      .filter((message): message is { role: AgentMessage['role']; text: string } => Boolean(message));

    if (recentMessages.length === 0) return context;

    return {
      ...context,
      assistantContext: {
        ...(context.assistantContext ?? {}),
        recentMessages,
      },
    };
  }

  private inferRequiredFields(input: string, allFields: string[]): string[] {
    const q = input.toLowerCase();
    const required = new Set<string>(['name', 'title', 'department', 'managerId']);
    if (/(promot|succession|exit|leav|replac|backfill|reorg|reorganize|restructure|scenario)/.test(q)) {
      required.add('directReports');
      required.add('level');
    }
    if (/(flight risk|retention|attrition)/.test(q)) required.add('flightRisk');
    if (/(performance|9-box|nine-box)/.test(q)) required.add('performance');
    if (/(downsize|layoff|lay.?off|cut|eliminat|reduc.*role|headcount reduc)/.test(q)) {
      // For downsizing, surface performance/rating fields so the LLM can see what ranking data exists
      required.add('performance');
      required.add('overallRating');
      required.add('nineBoxPlacement');
      required.add('salary');
      required.add('band');
    }
    if (/(potential|readiness|successor)/.test(q)) {
      required.add('potential');
      required.add('readiness');
    }
    if (/(salary|pay|comp|compensation|band)/.test(q)) {
      required.add('salary');
      required.add('band');
    }
    if (/(engagement|enps)/.test(q)) required.add('engagementScore');
    return Array.from(required).filter((field) => allFields.includes(field));
  }

  private async buildCompactContextPayload(
    context: IAgentContext,
    requiredFields: string[],
  ): Promise<Record<string, unknown>> {
    const nodes = context.diagramData.nodes;
    const selectedIds = new Set(context.selectedNodeIds);

    const departments = new Map<string, number>();
    const titles = new Map<string, number>();
    const reportsByManager = computeOrgReportCounts(nodes, context.diagramData.edges);

    // Extra fields beyond the always-present ones — include values when present so the LLM can rank
    const extraFieldNames = requiredFields.filter(
      (f) => !['name', 'title', 'department', 'managerId'].includes(f),
    );

    const samplePeople: Array<Record<string, unknown>> = [];
    for (const node of nodes.slice(0, 200)) {
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const props = (node.props ?? {}) as Record<string, unknown>;
      const name = (node.name ?? attrs.name ?? props.name) as string | undefined;
      const title = (attrs.title ?? props.title ?? attrs.role ?? props.role) as string | undefined;
      const dept = (attrs.department ?? props.department) as string | undefined;
      if (dept && typeof dept === 'string') departments.set(dept, (departments.get(dept) ?? 0) + 1);
      if (title && typeof title === 'string') titles.set(title, (titles.get(title) ?? 0) + 1);

      if (samplePeople.length < 80) {
        const entry: Record<string, unknown> = {
          id: node.id,
          name,
          title,
          department: dept,
          directReports: reportsByManager.get(node.id)?.directReports ?? 0,
          selected: selectedIds.has(node.id),
        };
        // Include available extra field values for ranking/filtering
        for (const field of extraFieldNames) {
          const val = attrs[field] ?? props[field];
          if (val !== undefined) entry[field] = val;
        }
        samplePeople.push(entry);
      }
    }

    const topDepartments = Array.from(departments.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name, count }));
    const topTitles = Array.from(titles.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([title, count]) => ({ title, count }));

    const availablePresets = (context.stores.viewRules.fullPresets ?? [])
      .map((p) => p.name)
      .slice(0, 20);

    const rdmPayload = await this.buildRdmContextPayload(context);

    // For network diagrams, expose the names + types of every Frame
    // (VPCs/subnets/accounts) so the LLM knows what `placeInside`
    // values are valid. Without this it has to guess subnet names
    // and often gets "could not find container" failures.
    let frameSummary: Array<{ id: string; name: string; type: string }> | undefined;
    if (context.diagramType === 'network') {
      frameSummary = (context.diagramData.frames ?? [])
        .slice(0, 40)
        .map((frame) => ({
          id: frame.id,
          name: frame.name,
          type: String(frame.metadata?.containerType ?? 'frame'),
        }));
    }

    return {
      selectedNodeIds: context.selectedNodeIds,
      requiredFields,
      topDepartments,
      topTitles,
      peopleSample: samplePeople,
      availablePresets,
      rdmSummary: rdmPayload.rdmSummary,
      ...(rdmPayload.rdmContext ? { rdmContext: rdmPayload.rdmContext } : {}),
      ...(rdmPayload.selectedRdmContext ? { selectedRdmContext: rdmPayload.selectedRdmContext } : {}),
      ...(frameSummary && frameSummary.length > 0 ? { frameSummary } : {}),
      ...(context.assistantContext && typeof context.assistantContext === 'object'
        ? { assistantContext: context.assistantContext }
        : {}),
    };
  }

  private buildCompactGenogramContextPayload(context: IAgentContext): Record<string, unknown> {
    return buildGenogramSemanticContext(context);
  }

  async execute(
    input: string,
    context: IAgentContext,
    abortSignal?: AbortSignal,
    conversationHistory?: AgentMessage[],
  ): Promise<AgentExecutorResult> {
    const toolExecutionContext = { ...this.buildToolExecutionContext(context, conversationHistory), userRequest: input };
    const refreshToolContext = () => {
      if (context.getLatestDiagramData) toolExecutionContext.diagramData = context.getLatestDiagramData();
    };
    refreshToolContext();
    const toolPriority = Array.isArray(context.assistantContext?.toolPriority)
      ? context.assistantContext.toolPriority
        .map((entry) => String(entry ?? '').trim())
        .filter((entry) => entry.length > 0)
      : [];
    const availableTools = this.registry.getAvailableTools(context.diagramType, context.assistantContext)
      .filter(tool => context.canWrite || tool.tier !== 'structural');
    const structuredMatch = this.matcher.match(input, {
      toolPriority,
      availableTools: availableTools.map((tool) => tool.name),
    });
    if (structuredMatch && structuredMatch.confidence >= this.minStructuredConfidence) {
      const call: AgentToolCall = { name: structuredMatch.toolName, params: structuredMatch.params };
      const toolResults = await this.registry.executeToolCalls([call], toolExecutionContext);
      const text = toolResults[0]?.result.summary ?? 'Done.';
      return { text, toolResults, source: 'structured' };
    }

    const structuredQuery = supportsStructuredOrgQueries(context.diagramType)
      ? this.structuredQA.tryParse(input)
      : null;
    if (structuredQuery) {
      const nodes = context.diagramData.nodes.map((node) => ({
        id: node.id,
        type: node.type,
        data: {
          ...(node.attrs ?? {}),
          ...(node.props ?? {}),
          ...(node.name ? { name: node.name } : {}),
        },
      }));
      const edges = context.diagramData.edges.map((edge) => ({
        id: edge.id,
        type: edge.type,
        from: edge.from,
        to: edge.to,
        attrs: edge.attrs,
      }));
      const result = this.queryExecutor.execute(structuredQuery, {
        nodes,
        edges,
      });
      const toolResults = [{
        call: { name: 'structured_query', params: { type: structuredQuery.type, params: structuredQuery.params } },
        result: { success: result.success, summary: result.message, data: { nodeIds: result.nodeIds, count: result.count } },
      }];
      // A structured miss is a parse guess that did not pan out, not an
      // answer. With a model available, let it read the question properly
      // instead of returning "No one found matching" for a phrasing the
      // regex mangled; without one, the miss is the best we can say.
      if (result.success || !this.llmClient) {
        // Match the search tool: a structured hit should show on the canvas,
        // not only in the reply text.
        if (result.success && result.nodeIds.length > 0) {
          context.stores.highlight?.setHighlightedIds(new Set(result.nodeIds));
        }
        return { text: result.message, toolResults, source: 'structured' };
      }
    }

    if (!this.llmClient) {
      return { text: 'No tool matched and no LLM client configured.', toolResults: [], source: 'none' };
    }

    const maxSteps = 5;
    const allToolResults: Array<{ call: AgentToolCall; result: AgentToolResult }> = [];
    const availableNames = new Set(availableTools.map(tool => tool.name));
    const toolDefinitions = this.registry.buildToolDefinitions(context.diagramType, context.assistantContext).filter(tool => availableNames.has(tool.name));
    const buildDiagramSummary = async () => {
      const availableFields = toolExecutionContext.diagramData.fields.map((f) => f.id);
      const isGenogram = context.diagramType === 'genogram';
      const requiredFields = isGenogram ? [] : this.inferRequiredFields(input, availableFields);
      const executionPolicy = inferExecutionPolicy(input, context.diagramType);
      const contextPayload = isGenogram
        ? this.buildCompactGenogramContextPayload(toolExecutionContext)
        : await this.buildCompactContextPayload(toolExecutionContext, requiredFields);
      return {
        nodeCount: toolExecutionContext.diagramData.nodes.length,
        edgeCount: toolExecutionContext.diagramData.edges.length,
        fields: availableFields,
        ...(isGenogram ? {} : { availableTitles: this.collectAvailableTitles(toolExecutionContext) }),
        contextScope: {
          requiredFields,
          clarifyingQuestionScope: buildClarifyingScopeText(executionPolicy),
          contextShape: this.getContextShape(context.diagramType, isGenogram),
        },
        contextPayload: {
          ...contextPayload,
          canvasAccess: {canWrite: context.canWrite},
          executionPolicy,
        },
      };
    };

    const loopMessages: AgentMessage[] = [
      ...(conversationHistory ?? []),
      { role: 'user' as const, content: input },
    ];

    let finalText = '';
    let consecutiveFailSteps = 0;

    for (let step = 0; step < maxSteps; step++) {
      refreshToolContext();
      const diagramSummary = await buildDiagramSummary();
      const response = await this.llmClient({
        messages: loopMessages,
        diagramType: context.diagramType,
        diagramSummary,
        availableTools: availableTools.map((t) => t.name),
        toolDefinitions,
        uiLocale: context.uiLocale,
        abortSignal,
      });

      const hasToolCalls = response.toolCalls && response.toolCalls.length > 0;

      if (!hasToolCalls) {
        finalText = response.text;
        break;
      }

      // Append assistant message with tool calls
      loopMessages.push({
        role: 'assistant' as const,
        content: response.text,
        toolCalls: response.toolCalls,
      });

      // Execute tools locally
      refreshToolContext();
      const stepResults = await this.registry.executeToolCalls(response.toolCalls, toolExecutionContext);
      allToolResults.push(...stepResults);

      // Append tool results message
      loopMessages.push({
        role: 'tool' as const,
        content: stepResults.map((r) => ({
          toolCallId: r.call.id ?? r.call.name,
          toolName: r.call.name,
          result: JSON.stringify(r.result),
        })),
      });

      // Missing user input is terminal, not a transient tool failure to retry.
      const inputResults = stepResults.filter((r) => r.result.data?.needsInput === true);
      if (inputResults.length > 0) {
        finalText = inputResults.map((r) => r.result.summary).join('\n');
        break;
      }

      // Suggest-only step is terminal — if actual work also happened, continue.
      const hasSuccessfulSuggest = stepResults.some((r) => r.call.name === 'suggest' && r.result.success);
      const hasSuccessfulNonSuggest = stepResults.some((r) => r.call.name !== 'suggest' && r.result.success);
      if (hasSuccessfulSuggest && !hasSuccessfulNonSuggest) {
        finalText = response.text;
        break;
      }

      // Preview is terminal — user must confirm/reject before continuing
      const hasPreview = stepResults.some((r) => r.result.preview);
      if (hasPreview) {
        // Tool-confirmed status stays visible even when the model omits it.
        finalText = [response.text.trim(), ...stepResults.filter(r => r.result.preview).map(r => i18n.t('diagram:process.activity.changeSetAwaitingReview', {title: r.result.preview!.description, lng: context.uiLocale}))].filter(Boolean).join('\n\n');
        break;
      }

      // Form request is terminal — user must fill the form before continuing
      const hasFormRequest = stepResults.some((r) => r.result.formRequest);
      if (hasFormRequest) {
        finalText = response.text;
        break;
      }

      // Lens request is terminal — user must confirm view changes before continuing
      const hasLensRequest = stepResults.some((r) => r.result.lensRequest);
      if (hasLensRequest) {
        finalText = response.text;
        break;
      }

      // Track consecutive all-fail steps to avoid infinite error loops
      const allFailed = stepResults.length > 0 && stepResults.every((r) => !r.result.success);
      if (allFailed) {
        consecutiveFailSteps++;
        if (consecutiveFailSteps >= 2) {
          finalText = response.text || buildToolFailureResponse(stepResults);
          break;
        }
      } else {
        consecutiveFailSteps = 0;
      }

      // If this is the last step, use whatever text the LLM gave us
      if (step === maxSteps - 1) {
        finalText = response.text || allToolResults.filter((r) => r.result.success).map((r) => r.result.summary).join('\n') || 'Done.';
      }
    }

    // Preserve actionable input requirements so their recovery UI reaches chat.
    const visibleResults = allToolResults.filter((r) => r.result.success || r.result.data?.needsInput === true);
    // Tool-only completions still owe the user a result. Do not lose query
    // findings when the model finishes with an empty response or suggestions.
    if (!finalText.trim()) {
      finalText = visibleResults
        .filter((r) => this.registry.getTool(r.call.name)?.category === 'query')
        .map((r) => r.result.summary)
        .filter(Boolean)
        .join('\n\n');
    }
    if (!finalText.trim()) finalText = visibleResults.filter(r => r.call.name === 'suggest').map(r => r.result.summary).join('\n');
    return { text: withUnrecoveredFailures(finalText, allToolResults, this.registry), toolResults: visibleResults, source: 'llm' };
  }
}

/** Read-only tools: a failed lookup changes nothing the person could miss. */
const READ_ONLY_TOOL_CATEGORIES = new Set(['query', 'search']);

/**
 * The model's closing text is not evidence that a change happened. A change
 * that failed, and was not made by a later successful call to the same tool,
 * reaches the person with the tool's own reason, whatever the model wrote
 * after it ("I asked the agent to name a scenario and it said it did, but I
 * cannot find it", customer report 2026-10).
 */
function withUnrecoveredFailures(
  text: string,
  results: Array<{ call: AgentToolCall; result: AgentToolResult }>,
  registry: AgentToolRegistry,
): string {
  const reasons = new Set<string>();
  results.forEach((entry, index) => {
    if (entry.result.success || entry.result.data?.needsInput === true) return;
    const category = registry.getTool(entry.call.name)?.category;
    if (category && READ_ONLY_TOOL_CATEGORIES.has(category)) return;
    const recovered = results
      .slice(index + 1)
      .some((later) => later.call.name === entry.call.name && later.result.success);
    if (recovered) return;
    const reason = entry.result.summary?.trim() || `${entry.call.name} did not complete.`;
    if (!text.includes(reason)) reasons.add(reason);
  });
  if (reasons.size === 0) return text;
  const notes = Array.from(reasons, (reason) => `Not done: ${reason}`);
  return [text.trim(), ...notes].filter(Boolean).join('\n\n');
}

function buildToolFailureResponse(
  results: Array<{ result: AgentToolResult }>,
): string {
  const reason = results
    .map((entry) => entry.result.summary?.trim())
    .find((summary): summary is string => Boolean(summary));
  if (!reason) {
    return 'I need one more architecture detail before I can make that change. Tell me the target resource or where it should connect.';
  }
  return `I hit a diagram constraint: ${reason} Tell me the intended target or placement and I'll apply the change.`;
}
